import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { countryCodes, getDefaultCountry, getCountryByName } from "../data/countryCodes";
import { loginMember } from "../services/memberApi";
import { saveUserSession, registerUser, getUserSession, isUserLoggedIn, saveLoyaltyDiscount } from "../utils/auth";
import { fetchRedeemOverrideRules, getAvailablePoints } from "../services/loyaltyApi";
import { useTranslation } from "../context/TranslationContext";
import { sendWhatsAppOtp, verifyWhatsAppOtp } from "../services/whatsappApi";
import { sendEmailOtp, verifyEmailOtp } from "../services/emailOtpLoginApi";

const AuthModal = ({
  show,
  onClose,
  accommodationGroupDetail,
  accommodationDetail,
  memberPercent = 0
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // View state: 'methods' | 'email-signin' | 'email-verify' | 'email-register' | 'email-password' | 'whatsapp' | 'whatsapp-verify'
  const [currentView, setCurrentView] = useState('methods');

  // State for Sign In form
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInErrors, setSignInErrors] = useState({});
  const [isSignInLoading, setIsSignInLoading] = useState(false);
  const [signInSuccess, setSignInSuccess] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);

  // State for Register form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState(getDefaultCountry().code);
  const [mobileNumber, setMobileNumber] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registerErrors, setRegisterErrors] = useState({});
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  // State for dropdowns
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // State for WhatsApp form
  const [waCountryCode, setWaCountryCode] = useState(getDefaultCountry().code);
  const [waMobileNumber, setWaMobileNumber] = useState('');
  const [waErrors, setWaErrors] = useState({});
  const [isWaLoading, setIsWaLoading] = useState(false);
  const [showWaCountryDropdown, setShowWaCountryDropdown] = useState(false);
  const [waCountrySearchTerm, setWaCountrySearchTerm] = useState('');

  // State for WhatsApp OTP verification
  const [waPhoneNumber, setWaPhoneNumber] = useState(''); // display: mobileCode + number
  const [waSentMobileCode, setWaSentMobileCode] = useState(''); // mobileCode sent to API
  const [waSentNumber, setWaSentNumber] = useState(''); // phoneNumber sent to API
  const [otpCode, setOtpCode] = useState('');
  const [otpErrors, setOtpErrors] = useState({});
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [waResendCountdown, setWaResendCountdown] = useState(0);

  // State for Email OTP
  const [emailOtpEmail, setEmailOtpEmail] = useState('');
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpErrors, setEmailOtpErrors] = useState({});
  const [isEmailOtpLoading, setIsEmailOtpLoading] = useState(false);
  const [isEmailOtpVerifying, setIsEmailOtpVerifying] = useState(false);
  const [emailResendCountdown, setEmailResendCountdown] = useState(0);

  // Check if OTP login is enabled from accommodationGroupDetail
  const isOtpLoginEnabled = accommodationGroupDetail?.IsOtpLogin == 1 ||
    accommodationGroupDetail?.isOtpLogin == 1 ||
    accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsOtpLogin == 1;

  // Check if WhatsApp login is enabled from accommodationGroupDetail
  const isWhatsAppLoginEnabled = accommodationGroupDetail?.IsWhatsAppLogin == 1 ||
    accommodationGroupDetail?.isWhatsAppLogin == 1 ||
    accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsWhatsAppLogin == 1;

  // Check if Social/Google login is enabled from accommodationGroupDetail
  const isSocialLoginEnabled = accommodationGroupDetail?.IsSocialLogin == 1 ||
    accommodationGroupDetail?.isSocialLogin == 1 ||
    accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsSocialLogin == 1;

  // Reset to methods screen every time modal opens
  useEffect(() => {
    if (show) {
      setCurrentView(isOtpLoginEnabled ? 'methods' : 'email-password');
      setSignInErrors({});
      setRegisterErrors({});
      setWaErrors({});
      setWaMobileNumber('');
      setOtpCode('');
      setOtpErrors({});
      setWaPhoneNumber('');
      setEmailOtpEmail('');
      setEmailOtpCode('');
      setEmailOtpErrors({});
      setWaResendCountdown(0);
      setEmailResendCountdown(0);
    }
  }, [show]);

  // Countdown timer for WhatsApp resend
  useEffect(() => {
    if (waResendCountdown <= 0) return;
    const timer = setTimeout(() => setWaResendCountdown(waResendCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [waResendCountdown]);

  // Countdown timer for Email resend
  useEffect(() => {
    if (emailResendCountdown <= 0) return;
    const timer = setTimeout(() => setEmailResendCountdown(emailResendCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [emailResendCountdown]);

  // Set default country code based on hotel's country when modal opens
  useEffect(() => {
    if (show) {
      const hotelCountry = accommodationDetail?.CountryName ||
                          accommodationDetail?.countryName ||
                          accommodationDetail?.Country ||
                          accommodationDetail?.country ||
                          accommodationGroupDetail?.CountryName ||
                          accommodationGroupDetail?.countryName ||
                          accommodationGroupDetail?.Country ||
                          accommodationGroupDetail?.country ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.CountryName ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.countryName ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.Country ||
                          accommodationGroupDetail?.accommodationDetails?.[0]?.country ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.CountryName ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.countryName ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.Country ||
                          accommodationGroupDetail?.accommodationdetailsdb?.[0]?.country ||
                          accommodationGroupDetail?.hotelMapping?.CountryName ||
                          accommodationGroupDetail?.hotelMapping?.countryName ||
                          accommodationGroupDetail?.hotelMapping?.Country ||
                          accommodationGroupDetail?.hotelMapping?.country;

      if (hotelCountry) {
        const matchedCountry = getCountryByName(hotelCountry);
        if (matchedCountry) {
          setCountryCode(matchedCountry.code);
          setWaCountryCode(matchedCountry.code);
        }
      }
    }
  }, [show, accommodationDetail, accommodationGroupDetail]);

  // Handle Google OAuth callback response
  useEffect(() => {
    const encodedResponse = searchParams.get('response');
    if (!encodedResponse) return;

    try {
      // Fix: URL query strings convert + to space, restore it for base64
      const fixedBase64 = encodedResponse.replace(/ /g, '+');
      const json = atob(fixedBase64);
      const responseData = JSON.parse(json);

      // Clean up the response param from URL immediately
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('response');
      const remaining = newSearchParams.toString();
      const newUrl = remaining ? `${window.location.pathname}?${remaining}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      if (responseData.statusCode === 'INFO_DATA_NOT_FOUND') {
        return;
      }

      const userData = {
        profile: responseData.profile || responseData.data?.profile || null,
        data: responseData.data || responseData
      };

      const token = responseData.data?.accessToken || responseData.token || responseData.data?.token || responseData.accessToken || null;

      if (!token) {
        return;
      }

      saveUserSession(userData, token);
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));

      // Fetch loyalty data after Google login
      const fetchLoyaltyAfterGoogleLogin = async () => {
        try {
          const hotelId = searchParams.get('hotel_id');
          let profileId = null;

          if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
            const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
            profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.id || masterProfile?.ID;
          } else if (userData.profile && typeof userData.profile === 'object') {
            profileId = userData.profile.CrmProfileId || userData.profile.crmProfileId || userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.id || userData.profile.ID;
          }

          if (!profileId && userData.data) {
            if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
              const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
              profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.id || masterProfile?.ID;
            } else if (userData.data.profile && typeof userData.data.profile === 'object') {
              profileId = userData.data.profile.CrmProfileId || userData.data.profile.crmProfileId || userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.id || userData.data.profile.ID;
            }
            profileId = profileId || userData.data.CrmProfileId || userData.data.crmProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.id || userData.data.ID;
          }

          if (hotelId && profileId) {
            const loyaltyResponse = await fetchRedeemOverrideRules({
              accommodationId: hotelId,
              profileId: profileId
            });
            if (loyaltyResponse && loyaltyResponse.success) {
              saveLoyaltyDiscount(loyaltyResponse);
            }
          }

          // Fetch available loyalty points
          let masterProfileId = null;
          if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
            const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
            masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
          } else if (userData.profile && typeof userData.profile === 'object') {
            masterProfileId = userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id;
          } else if (userData.data) {
            if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
              const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
              masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
            } else if (userData.data.profile && typeof userData.data.profile === 'object') {
              masterProfileId = userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id;
            }
            masterProfileId = masterProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id;
          }

          if (masterProfileId) {
            try {
              const availablePointsResponse = await getAvailablePoints({
                crmProfileId: masterProfileId,
                categoryId: 2
              });
              if (availablePointsResponse) {
                localStorage.setItem('availableLoyaltyPoints', JSON.stringify(availablePointsResponse));
              }
            } catch (pointsError) {
            }
          }
        } catch (loyaltyError) {
        }
      };

      fetchLoyaltyAfterGoogleLogin();

    } catch (error) {
    }
  }, []);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateSignIn = () => {
    const errors = {};

    if (!signInEmail.trim()) {
      errors.email = t('auth.emailRequired');
    } else if (!validateEmail(signInEmail)) {
      errors.email = t('auth.invalidEmail');
    }

    if (!signInPassword) {
      errors.password = t('auth.passwordRequired');
    } else if (signInPassword.length < 6) {
      errors.password = t('auth.passwordMinLength');
    }

    setSignInErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRegister = () => {
    const errors = {};

    if (!firstName.trim()) {
      errors.firstName = t('auth.firstNameRequired');
    }

    if (!lastName.trim()) {
      errors.lastName = t('auth.lastNameRequired');
    }

    if (!mobileNumber.trim()) {
      errors.mobileNumber = t('auth.mobileRequired');
    } else if (!validatePhone(mobileNumber)) {
      errors.mobileNumber = t('auth.invalidMobile');
    }

    if (!registerEmail.trim()) {
      errors.email = t('auth.emailRequired');
    } else if (!validateEmail(registerEmail)) {
      errors.email = t('auth.invalidEmail');
    }

    if (!registerPassword) {
      errors.registerPassword = t('auth.passwordRequired');
    } else if (registerPassword.length < 6) {
      errors.registerPassword = t('auth.passwordMinLength');
    }

    setRegisterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Extract PartnerKey from accommodationGroupDetail
  const getPartnerKey = () => {
    // console.log('[AuthModal] accommodationGroupDetail:', JSON.stringify(accommodationGroupDetail, null, 2));
    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      const key = accommodationGroupDetail.accommodationDetails[0]?.PartnerKey ||
             accommodationGroupDetail.accommodationDetails[0]?.partnerKey ||
             accommodationGroupDetail.accommodationDetails[0]?.partner_key ||
             null;
      // console.log('[AuthModal] PartnerKey from accommodationDetails:', key);
      return key;
    }
    const key = accommodationGroupDetail?.PartnerKey ||
           accommodationGroupDetail?.partnerKey ||
           accommodationGroupDetail?.partner_key ||
           null;
    // console.log('[AuthModal] PartnerKey from root:', key);
    return key;
  };

  // Extract AccessKey from accommodationGroupDetail
  const getAccessKey = () => {
    if (accommodationGroupDetail?.accommodationdetailsdb && Array.isArray(accommodationGroupDetail.accommodationdetailsdb) && accommodationGroupDetail.accommodationdetailsdb.length > 0) {
      return accommodationGroupDetail.accommodationdetailsdb[0]?.AccessKey ||
             accommodationGroupDetail.accommodationdetailsdb[0]?.accessKey ||
             accommodationGroupDetail.accommodationdetailsdb[0]?.access_key ||
             null;
    }

    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      return accommodationGroupDetail.accommodationDetails[0]?.AccessKey ||
             accommodationGroupDetail.accommodationDetails[0]?.accessKey ||
             accommodationGroupDetail.accommodationDetails[0]?.access_key ||
             null;
    }

    return accommodationGroupDetail?.AccessKey ||
           accommodationGroupDetail?.accessKey ||
           accommodationGroupDetail?.access_key ||
           null;
  };

  // Get accommodationId from URL or accommodationGroupDetail
  const getAccommodationId = () => {
    const hotel_id = searchParams.get('hotel_id') ||
                     searchParams.get('accommodationid') ||
                     null;

    if (hotel_id) {
      return hotel_id;
    }

    if (accommodationGroupDetail?.accommodationdetailsdb && Array.isArray(accommodationGroupDetail.accommodationdetailsdb) && accommodationGroupDetail.accommodationdetailsdb.length > 0) {
      return accommodationGroupDetail.accommodationdetailsdb[0]?.AccommodationId ||
             accommodationGroupDetail.accommodationdetailsdb[0]?.accommodationId ||
             null;
    }

    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      return accommodationGroupDetail.accommodationDetails[0]?.AccommodationId ||
             accommodationGroupDetail.accommodationDetails[0]?.accommodationId ||
             null;
    }

    return accommodationGroupDetail?.accommodationId || null;
  };

  // Handle Google OAuth login
  const handleGoogleLogin = () => {
    const accommodationId = getAccommodationId();
    const accessKey = getAccessKey();

    // console.log('[AuthModal] Google Login - accommodationId:', accommodationId, 'accessKey:', accessKey);
    // console.log('[AuthModal] accommodationGroupDetail:', accommodationGroupDetail);

    if (!accommodationId || !accessKey) {
      setSignInErrors({
        general: t('auth.hotelInfoNotAvailable')
      });
      return;
    }

    const accKey = `${accommodationId}-${accessKey}`;
    const currentUrl = window.location.href;
    const googleAuthUrl = `https://crm-v2.bookingwhizz.com/auth/google/redirect?accKey=${accKey}&url=${encodeURIComponent(currentUrl)}`;

    window.location.href = googleAuthUrl;
  };

  // Handle Sign In submit
  const handleSignInSubmit = async (e) => {
    e.preventDefault();

    if (!validateSignIn()) {
      return;
    }

    setIsSignInLoading(true);
    setSignInErrors({});
    setSignInSuccess(false);

    try {
      const partnerKey = getPartnerKey();

      const response = await loginMember({
        email: signInEmail.trim(),
        password: signInPassword,
        partnerKey: partnerKey
      });

      const statusCode = response.statusCode || response.status_code || response.StatusCode || '';
      const message = response.message || response.Message || '';

      if (statusCode === 'INFO_DATA_NOT_FOUND' || statusCode === 'DATA_NOT_FOUND' ||
          (message && (message.toLowerCase().includes('data was not found') ||
           message.toLowerCase().includes('data not found')))) {
        throw new Error(t('auth.noAccountFound'));
      }

      if (response.status === false || (response.success === false && !response.data)) {
        throw new Error(message || t('auth.loginFailed'));
      }

      setSignInSuccess(true);

      const userData = {
        profile: response.profile || response.data?.profile || null,
        data: response.data || null
      };

      const token = response.data?.accessToken || response.token || response.data?.token || null;

      if (!token) {
        throw new Error(t('auth.noTokenReceived'));
      }

      if (!userData.profile && !userData.data) {
        throw new Error(t('auth.noUserDataReceived'));
      }

      saveUserSession(userData, token);

      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));

      // Fetch redeem override rules after successful login
      try {
        const hotelId = searchParams.get('hotel_id');

        let profileId = null;

        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id || masterProfile?.id || masterProfile?.ID;
        }
        else if (userData.profile && typeof userData.profile === 'object') {
          profileId = userData.profile.CrmProfileId || userData.profile.crmProfileId || userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id || userData.profile.id || userData.profile.ID;
        }

        if (!profileId && userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id || masterProfile?.id || masterProfile?.ID;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            profileId = userData.data.profile.CrmProfileId || userData.data.profile.crmProfileId || userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id || userData.data.profile.id || userData.data.profile.ID;
          }
          profileId = profileId || userData.data.CrmProfileId || userData.data.crmProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id || userData.data.id || userData.data.ID;
        }

        if (hotelId && profileId) {
          const loyaltyResponse = await fetchRedeemOverrideRules({
            accommodationId: hotelId,
            profileId: profileId
          });

          if (loyaltyResponse && loyaltyResponse.success) {
            saveLoyaltyDiscount(loyaltyResponse);
          }
        }

        // Fetch available loyalty points
        let masterProfileId = null;
        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
        } else if (userData.profile && typeof userData.profile === 'object') {
          masterProfileId = userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id;
        } else if (userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            masterProfileId = userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id;
          }
          masterProfileId = masterProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id;
        }

        if (masterProfileId) {
          try {
            const availablePointsResponse = await getAvailablePoints({
              crmProfileId: masterProfileId,
              categoryId: 2
            });

            if (availablePointsResponse) {
              try {
                localStorage.setItem('availableLoyaltyPoints', JSON.stringify(availablePointsResponse));
              } catch (error) {
              }
            }
          } catch (pointsError) {
          }
        }
      } catch (loyaltyError) {
      }

      setTimeout(() => {
        onClose();
        setSignInEmail('');
        setSignInPassword('');
        setSignInSuccess(false);
      }, 1000);

    } catch (error) {
      setSignInErrors({
        general: error.message || t('auth.loginFailed')
      });
    } finally {
      setIsSignInLoading(false);
    }
  };

  // Handle Register submit
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!validateRegister()) {
      return;
    }

    setIsRegisterLoading(true);
    setRegisterErrors({});
    setRegisterSuccess(false);

    try {
      const hotel_id = searchParams.get('hotel_id') ||
                       searchParams.get('accommodationid') ||
                       accommodationGroupDetail?.accommodationId ||
                       null;

      const AccessKey = getAccessKey();

      if (!hotel_id || !AccessKey) {
        throw new Error(t('auth.hotelIdRequired'));
      }

      const selectedCountry = countryCodes.find(c => c.code === countryCode);
      const countrycode = selectedCountry?.code || '';
      const mobilecode = countryCode || selectedCountry?.code || '';

      const response = await registerUser({
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        email: registerEmail.trim(),
        password: registerPassword,
        confirmpassword: registerPassword,
        telephone: mobileNumber.trim(),
        countrycode: countrycode,
        mobilecode: mobilecode,
        hotel_id: hotel_id,
        AccessKey: AccessKey,
        currentUrl: window.location.href
      });

      const verificationMessage = response.message || response.data?.message || '';
      const isVerificationRequired = response.success && (
        verificationMessage.toLowerCase().includes('verification') ||
        verificationMessage.toLowerCase().includes('verify') ||
        verificationMessage.toLowerCase().includes('email sent')
      );

      if (isVerificationRequired) {
        setRegisterSuccess(true);

        setRegisterErrors({
          verification: verificationMessage || t('auth.verifyEmailMessage')
        });

        setTimeout(() => {
          setCurrentView('email-signin');
          setFirstName('');
          setLastName('');
          setCountryCode(getDefaultCountry().code);
          setMobileNumber('');
          setRegisterPassword('');
          setRegisterSuccess(false);
          const emailToKeep = registerEmail.trim();
          setRegisterEmail('');
          setSignInEmail(emailToKeep);
          setTimeout(() => {
            setRegisterErrors({});
          }, 500);
        }, 3000);
      } else {
        setRegisterSuccess(true);

        const userData = {
          profile: response.profile || response.data?.profile || {
            FirstName: firstName.trim(),
            LastName: lastName.trim(),
            Email: registerEmail.trim()
          },
          data: response.data || response || null
        };
        const token = response.data?.accessToken || response.token || response.data?.token || response.accessToken || null;

        if (token) {
          saveUserSession(userData, token);
          window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));
        }

        setTimeout(() => {
          onClose();
          setFirstName('');
          setLastName('');
          setCountryCode(getDefaultCountry().code);
          setMobileNumber('');
          setRegisterEmail('');
          setRegisterPassword('');
          setConfirmPassword('');
          setRegisterSuccess(false);
        }, 1500);
      }

    } catch (error) {
      setRegisterErrors({
        general: error.message || t('auth.registrationFailed')
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  // Handle WhatsApp Send OTP
  const handleWhatsAppSendOtp = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!waMobileNumber.trim()) {
      errors.mobileNumber = t('auth.mobileRequired');
    } else if (!validatePhone(waMobileNumber)) {
      errors.mobileNumber = t('auth.invalidMobile');
    }

    setWaErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsWaLoading(true);
    try {
      const partnerKey = getPartnerKey();
      if (!partnerKey) {
        throw new Error(t('auth.hotelInfoNotAvailable'));
      }

      // Send mobileCode and phoneNumber separately
      const phoneNumber = waMobileNumber.trim().replace(/\s/g, '');
      const mobileCode = waCountryCode; // e.g. "+92"

      const response = await sendWhatsAppOtp({
        phoneNumber,
        mobileCode,
        partnerKey
      });

      // OTP sent successfully - navigate to OTP verification view
      setWaErrors({});
      setWaPhoneNumber(mobileCode + phoneNumber);
      setWaSentMobileCode(mobileCode);
      setWaSentNumber(phoneNumber);
      setOtpCode('');
      setOtpErrors({});
      setWaResendCountdown(30);
      setCurrentView('whatsapp-verify');
    } catch (error) {
      setWaErrors({ general: error.message || 'Failed to send OTP' });
    } finally {
      setIsWaLoading(false);
    }
  };

  // Handle Email Send OTP
  const handleEmailSendOtp = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!signInEmail.trim()) {
      errors.email = t('auth.emailRequired');
    } else if (!validateEmail(signInEmail)) {
      errors.email = t('auth.invalidEmail');
    }

    setSignInErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsEmailOtpLoading(true);
    try {
      const partnerKey = getPartnerKey();
      if (!partnerKey) {
        throw new Error(t('auth.hotelInfoNotAvailable'));
      }

      await sendEmailOtp({
        email: signInEmail.trim(),
        partnerKey
      });

      setSignInErrors({});
      setEmailOtpEmail(signInEmail.trim());
      setEmailOtpCode('');
      setEmailOtpErrors({});
      setEmailResendCountdown(30);
      setCurrentView('email-verify');
    } catch (error) {
      setSignInErrors({ general: error.message || 'Failed to send OTP' });
    } finally {
      setIsEmailOtpLoading(false);
    }
  };

  // Handle Email OTP Verify
  const handleEmailVerifyOtp = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!emailOtpCode.trim()) {
      errors.otp = t('auth.otpRequired');
    } else if (emailOtpCode.trim().length < 4) {
      errors.otp = t('auth.invalidOtp');
    }

    setEmailOtpErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsEmailOtpVerifying(true);
    try {
      const partnerKey = getPartnerKey();
      if (!partnerKey) {
        throw new Error(t('auth.hotelInfoNotAvailable'));
      }

      const response = await verifyEmailOtp({
        email: emailOtpEmail,
        code: emailOtpCode.trim(),
        partnerKey
      });

      if (response.status === false) {
        throw new Error(response.message || t('auth.loginFailed'));
      }

      setEmailOtpErrors({});

      const userData = {
        profile: response.profile || response.data?.profile || null,
        data: response.data || null
      };

      const token = response.data?.accessToken || response.token || response.data?.token || null;

      if (!token) {
        throw new Error(t('auth.noTokenReceived'));
      }

      saveUserSession(userData, token);
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));

      // Fetch loyalty data after login
      try {
        const hotelId = searchParams.get('hotel_id');
        let profileId = null;

        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.id || masterProfile?.ID;
        } else if (userData.profile && typeof userData.profile === 'object') {
          profileId = userData.profile.CrmProfileId || userData.profile.crmProfileId || userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.id || userData.profile.ID;
        }

        if (!profileId && userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.id || masterProfile?.ID;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            profileId = userData.data.profile.CrmProfileId || userData.data.profile.crmProfileId || userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.id || userData.data.profile.ID;
          }
          profileId = profileId || userData.data.CrmProfileId || userData.data.crmProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.id || userData.data.ID;
        }

        if (hotelId && profileId) {
          const loyaltyResponse = await fetchRedeemOverrideRules({
            accommodationId: hotelId,
            profileId: profileId
          });
          if (loyaltyResponse && loyaltyResponse.success) {
            saveLoyaltyDiscount(loyaltyResponse);
          }
        }

        // Fetch available loyalty points
        let masterProfileId = null;
        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
        } else if (userData.profile && typeof userData.profile === 'object') {
          masterProfileId = userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id;
        } else if (userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            masterProfileId = userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id;
          }
          masterProfileId = masterProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id;
        }

        if (masterProfileId) {
          try {
            const availablePointsResponse = await getAvailablePoints({
              crmProfileId: masterProfileId,
              categoryId: 2
            });
            if (availablePointsResponse) {
              localStorage.setItem('availableLoyaltyPoints', JSON.stringify(availablePointsResponse));
            }
          } catch (pointsError) {
          }
        }
      } catch (loyaltyError) {
      }

      setTimeout(() => {
        onClose();
        setEmailOtpCode('');
        setEmailOtpErrors({});
        setSignInEmail('');
      }, 1000);

    } catch (error) {
      setEmailOtpErrors({ general: error.message || 'Verification failed' });
    } finally {
      setIsEmailOtpVerifying(false);
    }
  };

  // Handle Email Resend OTP
  const handleEmailResendOtp = async () => {
    if (isEmailOtpLoading || emailResendCountdown > 0) return;
    setIsEmailOtpLoading(true);
    setEmailOtpErrors({});
    try {
      const partnerKey = getPartnerKey();
      if (!partnerKey) {
        throw new Error(t('auth.hotelInfoNotAvailable'));
      }
      await sendEmailOtp({
        email: emailOtpEmail,
        partnerKey
      });
      setEmailOtpCode('');
      setEmailOtpErrors({ success: t('auth.otpResent') });
      setEmailResendCountdown(30);
    } catch (error) {
      setEmailOtpErrors({ general: error.message || 'Failed to resend OTP' });
    } finally {
      setIsEmailOtpLoading(false);
    }
  };

  // Handle WhatsApp OTP Verify
  const handleWhatsAppVerifyOtp = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!otpCode.trim()) {
      errors.otp = t('auth.otpRequired');
    } else if (otpCode.trim().length < 4) {
      errors.otp = t('auth.invalidOtp');
    }

    setOtpErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsOtpVerifying(true);
    try {
      const partnerKey = getPartnerKey();
      if (!partnerKey) {
        throw new Error(t('auth.hotelInfoNotAvailable'));
      }

      const response = await verifyWhatsAppOtp({
        phoneNumber: waSentNumber,
        code: otpCode.trim(),
        mobileCode: waSentMobileCode,
        partnerKey
      });

      // console.log('WhatsApp Login Response:', response);

      if (response.status === false) {
        throw new Error(response.message || t('auth.loginFailed'));
      }

      setOtpErrors({});

      const userData = {
        profile: response.profile || response.data?.profile || null,
        data: response.data || null
      };

      const token = response.data?.accessToken || response.token || response.data?.token || null;

      if (!token) {
        throw new Error(t('auth.noTokenReceived'));
      }

      saveUserSession(userData, token);
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: { user: userData, token } }));

      // Fetch loyalty data after login
      try {
        const hotelId = searchParams.get('hotel_id');
        let profileId = null;

        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.id || masterProfile?.ID;
        } else if (userData.profile && typeof userData.profile === 'object') {
          profileId = userData.profile.CrmProfileId || userData.profile.crmProfileId || userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.id || userData.profile.ID;
        }

        if (!profileId && userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId || masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.id || masterProfile?.ID;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            profileId = userData.data.profile.CrmProfileId || userData.data.profile.crmProfileId || userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.id || userData.data.profile.ID;
          }
          profileId = profileId || userData.data.CrmProfileId || userData.data.crmProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.id || userData.data.ID;
        }

        if (hotelId && profileId) {
          const loyaltyResponse = await fetchRedeemOverrideRules({
            accommodationId: hotelId,
            profileId: profileId
          });
          if (loyaltyResponse && loyaltyResponse.success) {
            saveLoyaltyDiscount(loyaltyResponse);
          }
        }

        // Fetch available loyalty points
        let masterProfileId = null;
        if (userData.profile && Array.isArray(userData.profile) && userData.profile.length > 0) {
          const masterProfile = userData.profile.find(p => p.masterProfile || p.MasterProfile) || userData.profile[0];
          masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
        } else if (userData.profile && typeof userData.profile === 'object') {
          masterProfileId = userData.profile.MasterProfileId || userData.profile.masterProfileId || userData.profile.master_profile_id;
        } else if (userData.data) {
          if (userData.data.profile && Array.isArray(userData.data.profile) && userData.data.profile.length > 0) {
            const masterProfile = userData.data.profile.find(p => p.masterProfile || p.MasterProfile) || userData.data.profile[0];
            masterProfileId = masterProfile?.MasterProfileId || masterProfile?.masterProfileId || masterProfile?.master_profile_id;
          } else if (userData.data.profile && typeof userData.data.profile === 'object') {
            masterProfileId = userData.data.profile.MasterProfileId || userData.data.profile.masterProfileId || userData.data.profile.master_profile_id;
          }
          masterProfileId = masterProfileId || userData.data.MasterProfileId || userData.data.masterProfileId || userData.data.master_profile_id;
        }

        if (masterProfileId) {
          try {
            const availablePointsResponse = await getAvailablePoints({
              crmProfileId: masterProfileId,
              categoryId: 2
            });
            if (availablePointsResponse) {
              localStorage.setItem('availableLoyaltyPoints', JSON.stringify(availablePointsResponse));
            }
          } catch (pointsError) {
          }
        }
      } catch (loyaltyError) {
      }

      setTimeout(() => {
        onClose();
        setOtpCode('');
        setOtpErrors({});
        setWaMobileNumber('');
      }, 1000);

    } catch (error) {
      setOtpErrors({ general: error.message || 'Verification failed' });
    } finally {
      setIsOtpVerifying(false);
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    if (isWaLoading || waResendCountdown > 0) return;
    setIsWaLoading(true);
    setOtpErrors({});
    try {
      const partnerKey = getPartnerKey();
      if (!partnerKey) {
        throw new Error(t('auth.hotelInfoNotAvailable'));
      }
      await sendWhatsAppOtp({
        phoneNumber: waSentNumber,
        mobileCode: waSentMobileCode,
        partnerKey
      });
      setOtpCode('');
      setOtpErrors({ success: t('auth.otpResent') });
      setWaResendCountdown(30);
    } catch (error) {
      setOtpErrors({ general: error.message || 'Failed to resend OTP' });
    } finally {
      setIsWaLoading(false);
    }
  };

  // Back arrow component
  const BackArrow = ({ onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="p-1 rounded-full hover:bg-white/20 transition-colors"
    >
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );

  // Close button component
  const CloseButton = () => (
    <button
      type="button"
      onClick={onClose}
      className="p-1 rounded-full hover:bg-white/20 transition-colors"
    >
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );

  // Header bar for sub-views
  const ViewHeader = ({ title, onBack }) => (
    <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl" style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}>
      <div className="flex items-center gap-2">
        <BackArrow onClick={onBack} />
        <h3 className="text-white font-semibold text-base">{title}</h3>
      </div>
      <CloseButton />
    </div>
  );

  // Google SVG icon
  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  // WhatsApp SVG icon
  const WhatsAppIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );

  // Email SVG icon
  const EmailIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  // Eye open/close SVG for password toggle
  const EyeIcon = ({ visible }) => visible ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md bg-black/40 singup-modal overflow-y-auto py-8"
      onClick={(currentView === 'whatsapp-verify' || currentView === 'email-verify') ? undefined : onClose}
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative animate-fadeInUp flex flex-col z-[10000]"
        onClick={e => e.stopPropagation()}
        style={{ zIndex: 10000 }}
      >
        {/* ===== METHODS SCREEN ===== */}
        {currentView === 'methods' && (
          <>
            <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl" style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}>
              <h3 className="text-white font-semibold text-base">{t('auth.chooseSignInMethod')}</h3>
              <CloseButton />
            </div>
            <div className="p-6 space-y-4">
              {/* General error message (e.g. Google login missing hotel info) */}
              {signInErrors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {signInErrors.general}
                </div>
              )}

              {/* WhatsApp Button - only show if IsWhatsAppLogin == 1 */}
              {isWhatsAppLoginEnabled && (
              <button
                type="button"
                onClick={() => setCurrentView('whatsapp')}
                className="w-full flex items-center justify-center gap-3 text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90"
                style={{ backgroundColor: '#25D366' }}
              >
                <WhatsAppIcon />
                {t('auth.signInWithWhatsApp')}
              </button>
              )}

              {/* Email Button */}
              <button
                type="button"
                onClick={() => setCurrentView('email-signin')}
                className="w-full flex items-center justify-center gap-3 text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90"
                style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}
              >
                <EmailIcon />
                {t('auth.signInWithEmail')}
              </button>

              {/* Google Button */}
              {isSocialLoginEnabled && (
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 rounded-lg transition-all text-sm shadow-sm hover:shadow-md"
              >
                <GoogleIcon />
                {t('auth.continueWithGoogle')}
              </button>
              )}

              {/* Why sign in — premium member-benefits panel */}
              <div className="mt-1.5 overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white px-5 pt-4 pb-3.5 shadow-sm">
                <p className="mb-4 text-center text-[13.5px] font-bold leading-snug tracking-tight text-gray-800">
                  {Number(memberPercent) > 0
                    ? t('loyalty.unlockMembersDeal', { percent: Number(memberPercent) })
                    : <><span aria-hidden="true">✨ </span>{t('auth.whySignInTitle')}</>}
                </p>
                <ul className="space-y-3.5">
                  {[t('auth.benefitRates'), t('auth.benefitBestPrice'), t('auth.benefitCheckout')].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3 text-[13px] font-medium leading-snug text-gray-700">
                      <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-emerald-100 ring-1 ring-emerald-200/60">
                        <svg className="h-3.5 w-3.5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center justify-center gap-1.5 border-t border-gray-100 pt-3 text-[11px] font-medium text-gray-400">
                  <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('auth.signInFootnote')}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== EMAIL SIGN IN VIEW ===== */}
        {currentView === 'email-signin' && (
          <>
            <ViewHeader title={t('auth.signInWithEmail')} onBack={() => setCurrentView('methods')} />
            <div className="p-6">
              <form className="space-y-4" autoComplete="off" onSubmit={handleEmailSendOtp}>
                {/* Email Input */}
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={signInEmail}
                    onChange={(e) => {
                      setSignInEmail(e.target.value);
                      if (signInErrors.email) {
                        setSignInErrors({ ...signInErrors, email: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      signInErrors.email ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.emailAddress')}
                  />
                  {signInErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{signInErrors.email}</p>
                  )}
                </div>

                {/* General error message */}
                {signInErrors.general && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {signInErrors.general}
                  </div>
                )}

                {/* Send OTP Button */}
                <button
                  type="submit"
                  disabled={isEmailOtpLoading}
                  className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90 ${
                    isEmailOtpLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}
                >
                  {isEmailOtpLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.sendingOtp')}
                    </span>
                  ) : (
                    <>
                      <EmailIcon />
                      {t('auth.sendOtp')}
                    </>
                  )}
                </button>

              </form>
            </div>
          </>
        )}

        {/* ===== EMAIL OTP VERIFY VIEW ===== */}
        {currentView === 'email-verify' && (
          <>
            <ViewHeader title={t('auth.verifyOtp')} onBack={() => setCurrentView('email-signin')} />
            <div className="p-6">
              <form className="space-y-4" autoComplete="off" onSubmit={handleEmailVerifyOtp}>
                {/* OTP sent message */}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ backgroundColor: 'rgba(155, 30, 52, 0.12)' }}>
                    <svg className="w-7 h-7" fill="none" stroke="var(--primary-color, #9B1E34)" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('auth.otpSentToEmail')} <span className="font-semibold text-gray-800">{emailOtpEmail}</span>
                  </p>
                </div>

                {/* OTP Input */}
                <div>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={emailOtpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setEmailOtpCode(val);
                      if (emailOtpErrors.otp) {
                        setEmailOtpErrors({ ...emailOtpErrors, otp: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      emailOtpErrors.otp ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-2xl text-center py-3 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors tracking-[0.5em]`}
                    placeholder="------"
                  />
                  {emailOtpErrors.otp && (
                    <p className="text-red-500 text-xs mt-1 text-center">{emailOtpErrors.otp}</p>
                  )}
                </div>

                {/* Success message (OTP resent) */}
                {emailOtpErrors.success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm text-center">
                    {emailOtpErrors.success}
                  </div>
                )}

                {/* General error message */}
                {emailOtpErrors.general && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {emailOtpErrors.general}
                  </div>
                )}

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={isEmailOtpVerifying}
                  className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90 ${
                    isEmailOtpVerifying ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}
                >
                  {isEmailOtpVerifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.verifyingOtp')}
                    </span>
                  ) : (
                    t('auth.verifyOtp')
                  )}
                </button>

                {/* Resend OTP */}
                <div className="text-center text-sm text-gray-600 pt-1">
                  {t('auth.didntReceiveOtp')}{' '}
                  <button
                    type="button"
                    onClick={handleEmailResendOtp}
                    disabled={isEmailOtpLoading || emailResendCountdown > 0}
                    className={`font-semibold hover:underline ${(isEmailOtpLoading || emailResendCountdown > 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ color: 'var(--primary-color, #9B1E34)' }}
                  >
                    {isEmailOtpLoading ? t('auth.sendingOtp') : emailResendCountdown > 0 ? `${t('auth.resendOtp')} (${emailResendCountdown}s)` : t('auth.resendOtp')}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {/* ===== EMAIL REGISTER VIEW ===== */}
        {currentView === 'email-register' && (
          <>
            <ViewHeader title={t('auth.register')} onBack={() => setCurrentView(isOtpLoginEnabled ? 'methods' : 'email-password')} />
            <div className="p-6">
              <form className="space-y-4" autoComplete="off" onSubmit={handleRegisterSubmit}>
                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      id="regfirst-name"
                      name="first-name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (registerErrors.firstName) {
                          setRegisterErrors({ ...registerErrors, firstName: '' });
                        }
                      }}
                      className={`block w-full bg-transparent border-0 border-b-2 ${
                        registerErrors.firstName ? 'border-red-500' : 'border-gray-300'
                      } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                      placeholder={t('auth.firstName')}
                    />
                    {registerErrors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{registerErrors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      id="reglast-name"
                      name="last-name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (registerErrors.lastName) {
                          setRegisterErrors({ ...registerErrors, lastName: '' });
                        }
                      }}
                      className={`block w-full bg-transparent border-0 border-b-2 ${
                        registerErrors.lastName ? 'border-red-500' : 'border-gray-300'
                      } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                      placeholder={t('auth.lastName')}
                    />
                    {registerErrors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{registerErrors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Mobile Code and Number */}
                <div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="w-full flex items-center justify-between bg-transparent border-0 border-b-2 border-gray-300 text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none transition-colors cursor-pointer"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-2xl leading-none">
                            {countryCodes.find(c => c.code === countryCode)?.flag || '🇮🇳'}
                          </span>
                          <span>{countryCode}</span>
                        </span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showCountryDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-[9998]"
                            onClick={() => setShowCountryDropdown(false)}
                          ></div>
                          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-[10000] max-h-64 overflow-y-auto w-80">
                            <div className="p-2">
                              <input
                                type="text"
                                placeholder={t('auth.searchCountry')}
                                value={countrySearchTerm}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setCountrySearchTerm(e.target.value)}
                              />
                              <div className="max-h-56 overflow-y-auto">
                                {countryCodes
                                  .filter(country => {
                                    const searchTerm = countrySearchTerm.toLowerCase().trim();
                                    if (!searchTerm) return true;
                                    return country.name.toLowerCase().includes(searchTerm) ||
                                      country.code.includes(searchTerm) ||
                                      country.iso.toLowerCase().includes(searchTerm);
                                  })
                                  .map((country, index) => (
                                  <button
                                    key={`${country.iso}-${index}`}
                                    type="button"
                                    onClick={() => {
                                      setCountryCode(country.code);
                                      setShowCountryDropdown(false);
                                      setCountrySearchTerm('');
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 transition-colors ${
                                      countryCode === country.code ? 'bg-blue-50' : ''
                                    }`}
                                  >
                                    <span className="text-xl">{country.flag}</span>
                                    <span className="flex-1 text-left font-medium">{country.name}</span>
                                    <span className="text-gray-600">{country.code}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => {
                        setMobileNumber(e.target.value);
                        if (registerErrors.mobileNumber) {
                          setRegisterErrors({ ...registerErrors, mobileNumber: '' });
                        }
                      }}
                      className={`w-full bg-transparent border-0 border-b-2 ${
                        registerErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                      } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                      placeholder={t('auth.mobileNumber')}
                    />
                  </div>
                  {registerErrors.mobileNumber && (
                    <p className="text-red-500 text-xs mt-1">{registerErrors.mobileNumber}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    id="regemail"
                    name="email"
                    value={registerEmail}
                    onChange={(e) => {
                      setRegisterEmail(e.target.value);
                      if (registerErrors.registerEmail) {
                        setRegisterErrors({ ...registerErrors, registerEmail: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      registerErrors.registerEmail ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.emailAddress')}
                  />
                  {registerErrors.registerEmail && (
                    <p className="text-red-500 text-xs mt-1">{registerErrors.registerEmail}</p>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showRegisterPassword ? "text" : "password"}
                    id="regpassword"
                    name="regpassword"
                    value={registerPassword}
                    onChange={(e) => {
                      setRegisterPassword(e.target.value);
                      if (registerErrors.registerPassword) {
                        setRegisterErrors({ ...registerErrors, registerPassword: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 pr-10 ${
                      registerErrors.registerPassword ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.createPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                    className="absolute right-0 top-0 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    <EyeIcon visible={showRegisterPassword} />
                  </button>
                  {registerErrors.registerPassword && (
                    <p className="text-red-500 text-xs mt-1">{registerErrors.registerPassword}</p>
                  )}
                </div>

                {/* Verification message */}
                {registerErrors.verification && (
                  <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-semibold mb-1">{t('auth.verificationEmailSent')}</p>
                        <p>{registerErrors.verification}</p>
                        <p className="mt-2 text-xs">{t('auth.checkEmailVerify')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {registerErrors.general && !registerErrors.verification && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {registerErrors.general}
                  </div>
                )}

                {registerSuccess && !registerErrors.verification && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    {t('auth.registrationSuccess')}
                  </div>
                )}

                <div className="flex gap-3 pt-3">
                  <button
                    type="submit"
                    disabled={isRegisterLoading}
                    className={`flex-1 text-white font-semibold py-2.5 rounded-lg transition-all text-sm shadow-md hover:shadow-lg ${
                      isRegisterLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}
                    onMouseEnter={(e) => {
                      if (!isRegisterLoading) e.target.style.backgroundColor = 'var(--primary-color, #8a1a2d)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isRegisterLoading) e.target.style.backgroundColor = 'var(--primary-color, #9B1E34)';
                    }}
                  >
                    {isRegisterLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth.registering')}
                      </span>
                    ) : (
                      t('auth.register')
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white border-2 font-semibold py-2.5 rounded-lg transition-all text-sm shadow-md hover:shadow-lg"
                    style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--primary-color)'; e.target.style.color = 'white'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = 'white'; e.target.style.color = 'var(--primary-color)'; }}
                  >
                    {t('auth.close')}
                  </button>
                </div>

                {/* Link to sign in */}
                <div className="text-center text-sm text-gray-600 pt-2">
                  {t('auth.alreadyHaveAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => { setCurrentView(isOtpLoginEnabled ? 'email-signin' : 'email-password'); setRegisterErrors({}); }}
                    className="font-semibold hover:underline"
                    style={{ color: 'var(--primary-color, #9B1E34)' }}
                  >
                    {t('auth.signIn')}
                  </button>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('auth.or')}</span>
                  </div>
                </div>

                {/* Google Login Button */}
                {isSocialLoginEnabled && (
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-2.5 rounded-lg transition-all text-sm shadow-sm hover:shadow-md"
                >
                  <GoogleIcon />
                  {t('auth.continueWithGoogle')}
                </button>
                )}
              </form>
            </div>
          </>
        )}

        {/* ===== EMAIL PASSWORD VIEW (non-OTP login) ===== */}
        {currentView === 'email-password' && (
          <>
            <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl" style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}>
              <h3 className="text-white font-semibold text-base">{t('auth.signIn')}</h3>
              <CloseButton />
            </div>
            <div className="p-6">
              <form className="space-y-4" autoComplete="off" onSubmit={handleSignInSubmit}>
                {/* Email Input */}
                <div>
                  <input
                    type="email"
                    id="ep-email"
                    name="email"
                    value={signInEmail}
                    onChange={(e) => {
                      setSignInEmail(e.target.value);
                      if (signInErrors.email) {
                        setSignInErrors({ ...signInErrors, email: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      signInErrors.email ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.emailAddress')}
                  />
                  {signInErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{signInErrors.email}</p>
                  )}
                </div>

                {/* Password Input */}
                <div className="relative">
                  <input
                    type={showSignInPassword ? 'text' : 'password'}
                    id="ep-password"
                    name="password"
                    value={signInPassword}
                    onChange={(e) => {
                      setSignInPassword(e.target.value);
                      if (signInErrors.password) {
                        setSignInErrors({ ...signInErrors, password: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      signInErrors.password ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base py-2.5 px-0 pr-10 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                    placeholder={t('auth.password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignInPassword(!showSignInPassword)}
                    className="absolute right-0 top-3 text-gray-400 hover:text-gray-600 p-1"
                  >
                    <EyeIcon visible={showSignInPassword} />
                  </button>
                  {signInErrors.password && (
                    <p className="text-red-500 text-xs mt-1">{signInErrors.password}</p>
                  )}
                </div>

                {/* Success message */}
                {signInSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    {t('auth.loginSuccess')}
                  </div>
                )}

                {/* General error message */}
                {signInErrors.general && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {signInErrors.general}
                  </div>
                )}

                {/* Sign In Button */}
                <button
                  type="submit"
                  disabled={isSignInLoading}
                  className={`w-full text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90 ${
                    isSignInLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ backgroundColor: 'var(--primary-color, #9B1E34)' }}
                >
                  {isSignInLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.signingIn')}
                    </span>
                  ) : (
                    t('auth.signIn')
                  )}
                </button>

                {/* Link to register */}
                <div className="text-center text-sm text-gray-600 pt-2">
                  {t('auth.noAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => { setCurrentView('email-register'); setSignInErrors({}); }}
                    className="font-semibold hover:underline"
                    style={{ color: 'var(--primary-color, #9B1E34)' }}
                  >
                    {t('auth.register')}
                  </button>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('auth.or')}</span>
                  </div>
                </div>

                {/* Google Login Button */}
                {isSocialLoginEnabled && (
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 rounded-lg transition-all text-sm shadow-sm hover:shadow-md"
                >
                  <GoogleIcon />
                  {t('auth.continueWithGoogle')}
                </button>
                )}
              </form>
            </div>
          </>
        )}

        {/* ===== WHATSAPP VIEW ===== */}
        {currentView === 'whatsapp' && (
          <>
            <ViewHeader title={t('auth.signInWithWhatsApp')} onBack={() => setCurrentView('methods')} />
            <div className="p-6">
              <form className="space-y-4" autoComplete="off" onSubmit={handleWhatsAppSendOtp}>
                {/* Country Code + Mobile Number */}
                <div>
                  <div className="grid grid-cols-[auto_1fr] gap-3">
                    {/* Country Code Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowWaCountryDropdown(!showWaCountryDropdown)}
                        className="w-full flex items-center justify-between bg-transparent border-0 border-b-2 border-gray-300 text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none transition-colors cursor-pointer min-w-[120px]"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-lg">
                            {countryCodes.find(c => c.code === waCountryCode)?.flag || '🇮🇳'}
                          </span>
                          <span>{waCountryCode}</span>
                        </span>
                        <svg className="w-4 h-4 text-gray-400 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showWaCountryDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-[9998]"
                            onClick={() => setShowWaCountryDropdown(false)}
                          ></div>
                          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-[10000] max-h-64 overflow-y-auto w-80">
                            <div className="p-2">
                              <input
                                type="text"
                                placeholder={t('auth.searchCountry')}
                                value={waCountrySearchTerm}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setWaCountrySearchTerm(e.target.value)}
                              />
                              <div className="max-h-56 overflow-y-auto">
                                {countryCodes
                                  .filter(country => {
                                    const searchTerm = waCountrySearchTerm.toLowerCase().trim();
                                    if (!searchTerm) return true;
                                    return country.name.toLowerCase().includes(searchTerm) ||
                                      country.code.includes(searchTerm) ||
                                      country.iso.toLowerCase().includes(searchTerm);
                                  })
                                  .map((country, index) => (
                                  <button
                                    key={`wa-${country.iso}-${index}`}
                                    type="button"
                                    onClick={() => {
                                      setWaCountryCode(country.code);
                                      setShowWaCountryDropdown(false);
                                      setWaCountrySearchTerm('');
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 transition-colors ${
                                      waCountryCode === country.code ? 'bg-blue-50' : ''
                                    }`}
                                  >
                                    <span className="text-xl">{country.flag}</span>
                                    <span className="flex-1 text-left font-medium">{country.name}</span>
                                    <span className="text-gray-600">{country.code}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Mobile Number Input */}
                    <input
                      type="tel"
                      value={waMobileNumber}
                      onChange={(e) => {
                        setWaMobileNumber(e.target.value);
                        if (waErrors.mobileNumber) {
                          setWaErrors({ ...waErrors, mobileNumber: '' });
                        }
                      }}
                      className={`w-full bg-transparent border-0 border-b-2 ${
                        waErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                      } text-gray-900 text-base py-2.5 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors`}
                      placeholder={t('auth.mobileNumber')}
                    />
                  </div>
                  {waErrors.mobileNumber && (
                    <p className="text-red-500 text-xs mt-1">{waErrors.mobileNumber}</p>
                  )}
                </div>

                {/* General error message */}
                {waErrors.general && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {waErrors.general}
                  </div>
                )}

                {/* Send OTP Button */}
                <button
                  type="submit"
                  disabled={isWaLoading}
                  className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90 ${
                    isWaLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ backgroundColor: '#25D366' }}
                >
                  {isWaLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.sendingOtp')}
                    </span>
                  ) : (
                    <>
                      <WhatsAppIcon />
                      {t('auth.sendOtp')}
                    </>
                  )}
                </button>
              </form>
            </div>
          </>
        )}
        {/* ===== WHATSAPP OTP VERIFY VIEW ===== */}
        {currentView === 'whatsapp-verify' && (
          <>
            <ViewHeader title={t('auth.verifyOtp')} onBack={() => setCurrentView('whatsapp')} />
            <div className="p-6">
              <form className="space-y-4" autoComplete="off" onSubmit={handleWhatsAppVerifyOtp}>
                {/* OTP sent message */}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ backgroundColor: '#25D36620' }}>
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#25D366">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('auth.otpSentTo')} <span className="font-semibold text-gray-800">{waPhoneNumber}</span>
                  </p>
                </div>

                {/* OTP Input */}
                <div>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setOtpCode(val);
                      if (otpErrors.otp) {
                        setOtpErrors({ ...otpErrors, otp: '' });
                      }
                    }}
                    className={`block w-full bg-transparent border-0 border-b-2 ${
                      otpErrors.otp ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-2xl text-center py-3 px-0 focus:ring-0 focus:outline-none placeholder:text-gray-400 transition-colors tracking-[0.5em]`}
                    placeholder="------"
                  />
                  {otpErrors.otp && (
                    <p className="text-red-500 text-xs mt-1 text-center">{otpErrors.otp}</p>
                  )}
                </div>

                {/* Success message (OTP resent) */}
                {otpErrors.success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm text-center">
                    {otpErrors.success}
                  </div>
                )}

                {/* General error message */}
                {otpErrors.general && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {otpErrors.general}
                  </div>
                )}

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={isOtpVerifying}
                  className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-lg transition-all text-sm shadow-md hover:shadow-lg hover:opacity-90 ${
                    isOtpVerifying ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  style={{ backgroundColor: '#25D366' }}
                >
                  {isOtpVerifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.verifyingOtp')}
                    </span>
                  ) : (
                    t('auth.verifyOtp')
                  )}
                </button>

                {/* Resend OTP */}
                <div className="text-center text-sm text-gray-600 pt-1">
                  {t('auth.didntReceiveOtp')}{' '}
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isWaLoading || waResendCountdown > 0}
                    className={`font-semibold hover:underline ${(isWaLoading || waResendCountdown > 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ color: '#25D366' }}
                  >
                    {isWaLoading ? t('auth.sendingOtp') : waResendCountdown > 0 ? `${t('auth.resendOtp')} (${waResendCountdown}s)` : t('auth.resendOtp')}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
